import { createClientAccount as createClientAccountApi, getClientAccounts, searchClientAccounts } from '@/api/clientAccounts';
import {
  IClientAccount,
  IClientAccountPaymentPlan
} from '@/models/clientAccount';
import { ActionContext } from 'vuex';

import { IState } from '..';
import { IObjectState, IStateObject } from '../';

export interface IStateClientAccount extends IObjectState, IClientAccount {
  active_plan_name: string;
  active_plan_status: string;
  owned_events_count: number;
}

export interface IClientAccountsState extends IStateObject<IStateClientAccount> {
  created: boolean;
}

const state: IClientAccountsState = {
  list: [],
  selected: [],
  loading: false,
  message: '',
  error: false,
  pages: 1,
  item_count: 0,
  created: false
};

const getActivePlan = (plans: IClientAccountPaymentPlan[]): IClientAccountPaymentPlan | undefined =>
  plans.find(plan => plan.subscription_status === 'active') || plans[0];

const addStateProps = (
  accounts: IClientAccount[]
): IStateClientAccount[] =>
  accounts.map(account => {
    const activePlan = getActivePlan(account.client_payment_plans || []);
    return {
      ...account,
      active_plan_name: activePlan ? activePlan.name : '',
      active_plan_status: activePlan ? activePlan.subscription_status : '',
      owned_events_count: (account.owned_events || []).length,
      loading: false,
      error: false,
      message: ''
    };
  });

const getters = {
  getClientAccounts: (state: IClientAccountsState) => state.list,
  getSelectedClientAccounts: (state: IClientAccountsState) => state.selected,
  getClientAccountsState: (state: IClientAccountsState) => state,
  getClientAccountsPages: (state: IClientAccountsState) => state.pages,
  getClientAccountsItemCount: (state: IClientAccountsState) => state.item_count,
};

const actions = {

  // Only fetches the page currently shown on screen, not the whole dataset.
  fetchClientAccounts(
    context: ActionContext<IClientAccountsState, IState>,
    params?: { page?: number, pageSize?: number, type?: string, search?: string }
  ) {

    context.commit('setClientAccountsLoading');

    const page = params?.page || 1;
    const pageSize = params?.pageSize || 20;
    const type = params?.type;
    const search = params?.search;

    // Numeric search terms are treated as an exact id lookup via the list
    // endpoint. Non-numeric terms use the dedicated search endpoint.
    if (search && !/^\d+$/.test(search)) {
      searchClientAccounts(page, pageSize, search)
        .then(response => {
          context.commit('setClientAccounts', addStateProps(response.data.results as IClientAccount[]));
          context.commit('setPages', response.data.page_count);
          context.commit('setItemCount', response.data.item_count);
        })
        .catch(err => {
          context.commit('setClientAccountsError', err.message);
        });
      return;
    }

    const id = search && /^\d+$/.test(search) ? parseInt(search, 10) : undefined;

    getClientAccounts(page, pageSize, '-id', type, id)
      .then(response => {
        context.commit('setClientAccounts', addStateProps(response.data.results));
        context.commit('setPages', response.data.page_count);
        context.commit('setItemCount', response.data.item_count);
      })
      .catch(err => {
        context.commit('setClientAccountsError', err.message);
      });

  },

  createClientAccount(
    context: ActionContext<IClientAccountsState, IState>,
    payload: { client_name: string, client_email: string, billing_name?: string, billing_vat_number?: string, billing_address?: string, billing_city?: string, billing_postal_number?: string, billing_country_code?: string }
  ) {
    context.commit('setClientAccountsLoading');

    createClientAccountApi(payload)
      .then(response => {
        context.commit('newClientAccount', response.data);
      })
      .catch(err => {
        context.commit('setClientAccountsError', err.response?.data || err.message);
      });
  },

};

const mutations = {

  setClientAccountsError: (state: IClientAccountsState, message: any) => (
    (state.loading = false),
    (state.error = true),
    (state.message = message)
  ),

  setClientAccountsLoading: (state: IClientAccountsState) => (
    (state.loading = true),
    (state.error = false)
  ),

  setClientAccounts: (state: IClientAccountsState, accounts: IStateClientAccount[]) => (
    (state.error = false),
    (state.loading = false),
    (state.list = accounts)
  ),

  newClientAccount: (state: IClientAccountsState, account: IClientAccount) => (
    (state.error = false),
    (state.loading = false),
    (state.created = true),
    (state.list = [addStateProps([account])[0], ...state.list])
  ),

  setPages: (state: IClientAccountsState, pages: number) => (
    (state.pages = pages)
  ),

  setItemCount: (state: IClientAccountsState, itemCount: number) => (
    (state.item_count = itemCount)
  ),

  selectClientAccount: (state: IClientAccountsState, account: IStateClientAccount) => {
    if (account.id) {
      const index = state.selected.indexOf(account.id);
      if (index > -1) {
        state.selected.splice(index, 1);
      } else {
        state.selected.push(account.id);
      }
    }
  },

};

export default {
  state,
  getters,
  actions,
  mutations
};
